import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Check, CreditCard, Loader2, Shield, ShieldCheck, Clock, FileText, Sparkles, ExternalLink, Gift, Timer, Lock, Users } from 'lucide-react';
import { toast } from 'sonner';

const SubscribePage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [claimingDiscount, setClaimingDiscount] = useState(false);
  const formRef = useRef(null);

  // Check URL params for payment status and fetch discount status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') === 'true') {
      toast.info('Payment was cancelled. You can try again when ready.');
    }
    if (params.get('failed') === 'true') {
      toast.error('Payment failed. Please try again or contact support.');
    }
    
    // Fetch discount status
    fetchDiscountStatus();
  }, []);

  const fetchDiscountStatus = async () => {
    try {
      const response = await api.get('/discount/status');
      setDiscountInfo(response.data);
    } catch (error) {
      console.log('Could not fetch discount status');
    }
  };

  const claimDiscount = async () => {
    setClaimingDiscount(true);
    try {
      const response = await api.post('/discount/claim-first-time');
      setDiscountInfo({
        has_discount: true,
        discount_code: response.data.discount_code,
        discounted_price: 22.50,
        original_price: 25.00,
        expires_at: response.data.expires_at
      });
      toast.success('Discount claimed! Valid for 24 hours.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not claim discount');
    } finally {
      setClaimingDiscount(false);
    }
  };

  const handleSubscribe = async (useDiscount = false) => {
    setLoading(true);

    try {
      // Create PayFast checkout session with discount flag
      const response = await subscriptionAPI.createPayfastCheckout(useDiscount);
      
      if (response.data.redirect_url && response.data.form_data) {
        // Create and submit a form to PayFast
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.redirect_url;
        
        // Add all form fields
        Object.entries(response.data.form_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        
        document.body.appendChild(form);
        form.submit();
      } else {
        toast.error('Failed to create payment session. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start payment. Please try again.');
      setLoading(false);
    }
  };

  const features = [
    { text: "Create up to 6 professional CVs", highlight: true },
    { text: "AI-powered CV enhancement & writing", highlight: true },
    { text: "AI Mock Interviewer to practice", highlight: true },
    { text: "Cover Letter Builder", highlight: false },
    { text: "Job description matching & ATS scoring", highlight: false },
    { text: "Unlimited PDF downloads (no watermarks)", highlight: false },
    { text: "Job application tracker", highlight: false },
    { text: "Access to all SA job resources", highlight: false },
  ];

  const isRenewal = user?.subscription_end_date && !user?.is_subscribed;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            {isRenewal ? 'Renew Your Access' : 'Unlock Your Career Potential'}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isRenewal ? 'Welcome Back!' : 'Get Started Today'}
          </h1>
          <p className="text-slate-400">
            {isRenewal 
              ? 'Your CVs and data are safe. Renew to continue using all features.'
              : 'One payment. 30 days of full access. Your data stays yours forever.'
            }
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Discount Banner */}
          {discountInfo?.has_discount && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Gift className="w-6 h-6" />
                  <div>
                    <p className="font-bold">First-Time Discount Active!</p>
                    <p className="text-sm text-amber-100">Pay R22.50 instead of R25</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-amber-100 flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    Expires in 24hrs
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Eligible for Discount Banner */}
          {discountInfo?.eligible_for_first_time && !discountInfo?.has_discount && (
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Gift className="w-6 h-6" />
                  <div>
                    <p className="font-bold">First-Time User? Get 10% Off!</p>
                    <p className="text-sm text-emerald-100">Pay R22.50 instead of R25</p>
                  </div>
                </div>
                <Button
                  onClick={claimDiscount}
                  disabled={claimingDiscount}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 text-sm font-bold px-4"
                >
                  {claimingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Claim Discount'}
                </Button>
              </div>
            </div>
          )}

          {/* Price Header */}
          <div className="bg-slate-900 p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-purple-500/10"></div>
            <div className="relative">
              {discountInfo?.has_discount ? (
                <>
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-3xl text-slate-400 line-through">R25</span>
                    <span className="text-5xl font-bold text-amber-400" style={{ fontFamily: 'Manrope, sans-serif' }}>R22.50</span>
                  </div>
                  <p className="text-amber-400 text-sm font-medium">You save R2.50!</p>
                </>
              ) : (
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>R25</span>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-slate-300 mt-2">
                <Clock className="w-4 h-4" />
                <span>30 Days Full Access</span>
              </div>
            </div>
          </div>

          {/* Value Props */}
          <div className="p-6 bg-emerald-50 border-b border-emerald-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-emerald-900">Your Data Is Always Safe</p>
                <p className="text-sm text-emerald-700">
                  Even after 30 days, your CVs and data are never deleted. Simply pay again to regain access.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Everything you get:
            </h3>
            <ul className="space-y-3 mb-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-700">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    feature.highlight ? 'bg-amber-100' : 'bg-slate-100'
                  }`}>
                    <Check className={`w-3 h-3 ${feature.highlight ? 'text-amber-600' : 'text-slate-500'}`} />
                  </div>
                  <span className={feature.highlight ? 'font-medium' : ''}>{feature.text}</span>
                </li>
              ))}
            </ul>

            {/* Payment Info */}
            <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium">Secure Payment via PayFast</p>
                  <p className="text-xs text-blue-700 mt-1">
                    You'll be redirected to PayFast's secure payment page. We accept Visa, Mastercard, Instant EFT, and SnapScan.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              data-testid="subscribe-btn"
              onClick={() => handleSubscribe(discountInfo?.has_discount || false)}
              disabled={loading}
              className={`w-full h-14 text-white text-lg font-bold rounded-xl shadow-lg transition-all hover:scale-[1.02] ${
                discountInfo?.has_discount 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-amber-500/30' 
                  : 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/30'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Redirecting to PayFast...
                </>
              ) : discountInfo?.has_discount ? (
                <>
                  <Gift className="w-5 h-5 mr-2" />
                  Pay R22.50 (10% Off!)
                  <ExternalLink className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  {isRenewal ? 'Renew Access - R25' : 'Get 30 Days Access - R25'}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            {/* Payment Methods Strip */}
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-xs text-slate-400 text-center mb-3 uppercase tracking-wider font-medium">Accepted Payment Methods</p>
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center gap-1.5 bg-[#1a1f71] text-white px-3 py-1.5 rounded-md" data-testid="badge-visa">
                  <span className="text-xs font-bold tracking-wide">VISA</span>
                </div>
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#eb001b] to-[#f79e1b] text-white px-3 py-1.5 rounded-md" data-testid="badge-mastercard">
                  <span className="text-xs font-bold tracking-wide">Mastercard</span>
                </div>
                <div className="flex items-center gap-1.5 bg-[#0b6623] text-white px-3 py-1.5 rounded-md" data-testid="badge-eft">
                  <span className="text-xs font-bold tracking-wide">Instant EFT</span>
                </div>
                <div className="flex items-center gap-1.5 bg-[#00a1e0] text-white px-3 py-1.5 rounded-md" data-testid="badge-snapscan">
                  <span className="text-xs font-bold tracking-wide">SnapScan</span>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="grid grid-cols-3 gap-3">
                {/* SSL Secure Badge */}
                <div className="flex flex-col items-center text-center p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid="trust-badge-ssl">
                  <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center mb-1.5">
                    <Lock className="w-4.5 h-4.5 text-emerald-600" />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">SSL Secure</span>
                  <span className="text-[10px] text-slate-400">256-bit encryption</span>
                </div>

                {/* Card Details Badge */}
                <div className="flex flex-col items-center text-center p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid="trust-badge-no-card">
                  <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center mb-1.5">
                    <ShieldCheck className="w-4.5 h-4.5 text-blue-600" />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">Card Safe</span>
                  <span className="text-[10px] text-slate-400">We never store card info</span>
                </div>

                {/* Social Proof Badge */}
                <div className="flex flex-col items-center text-center p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid="trust-badge-users">
                  <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center mb-1.5">
                    <Users className="w-4.5 h-4.5 text-amber-600" />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">500+ Users</span>
                  <span className="text-[10px] text-slate-400">SA job seekers trust us</span>
                </div>
              </div>
            </div>

            {/* PayFast Powered */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <Shield className="w-4 h-4 text-slate-400" />
              <p className="text-slate-500 text-xs">
                Payments processed securely by PayFast · SA's #1 payment gateway
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-slate-400 text-sm">
            No auto-renewal. No hidden fees. Pay only when you need access.
          </p>
          <p className="text-slate-500 text-xs">
            Questions? Email support@careerstep.co.za
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
